import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useForm } from 'react-hook-form';
import {
  Box,
  Dialog,
  DialogContent,
  Grid,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Typography,
} from '@mui/material';
import { Delete, DragIndicator, Edit, PictureAsPdf } from '@mui/icons-material';
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { fontModules, fontFormats, smallStyle, registerFonts } from '../../utils/QuillConfig';

import { AlertDialog, ConfirmDialog, DialogTitleClose } from '../dialog';
import { FormInputText, FormInputAutoComplete } from '../form';
import CustomInputSelect from '../custom/select/CustomInputSelect';
import * as addressBookActions from '../../store/addressBook';
import * as gclientActions from '../../store/gclient';
import * as userActions from '../../store/user';
import * as subMatTemplateActions from '../../store/subMatTemplate';
import SubMatTemplateTocTree from './SubMatTemplateTocTree';
import SubMatTemplateMergedTocPreview from './SubMatTemplateMergedTocPreview';
import {
  SubMatTemplateTocDocFrame,
  SubMatTemplateTocPreviewTitle,
  useSubMatTocDocPreview,
} from './SubMatTemplateTocDocPreview';
import SubMatTemplateExtraAttachmentsList, {
  DEFAULT_EXTRA_SECTION_TITLE,
} from './SubMatTemplateExtraAttachmentsList';
import SubMatTemplateSendSection from './SubMatTemplateSendSection';
import SubMatTemplateDialogFooter from './SubMatTemplateDialogFooter';
import SubMatDialogFooterButton from './SubMatDialogFooterButton';
import CertifiedStampPrintPane from './CertifiedStampPrintPane';
import { buildMergePreviewPayload, normalizeBodyHeaderPaging, normalizeTocNumbering } from './subMatTemplateTocOrder';
import {
  STAMP_LAYOUT_COMPACT,
  STAMP_LAYOUT_FULL,
  normalizeStampLayout,
} from './certifiedStampSpec';

const newExtraId = () => `extra-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
const STEP1_WORK_MIN_HEIGHT = 520;
const DIALOG_PAPER_SX = {
  width: { xs: 'calc(100vw - 32px)', md: 'min(1280px, calc(100vw - 64px))' },
  maxWidth: 'calc(100vw - 32px)',
  maxHeight: 'calc(100vh - 64px)',
};

const toSendDraft = (tpl, index) => ({
  key: String(tpl?.id || `tpl-${index}`),
  templateId: tpl?.id || null,
  name: tpl?.name || '',
  items: (tpl?.items || []).map((it) => ({ ...it })),
  extras: [],
  extraSectionTitle: DEFAULT_EXTRA_SECTION_TITLE,
  tocNumbering: normalizeTocNumbering(tpl?.tocNumbering),
  bodyHeaderPaging: normalizeBodyHeaderPaging(tpl?.bodyHeaderPaging),
  stampPlacements: tpl?.stampPlacements || {},
});

const draftHasContent = (draft) => {
  const itemCount = (draft?.items || []).filter((it) => it.included !== false).length;
  const extraCount = (draft?.extras || []).filter((e) => e.included !== false && e.file).length;
  return itemCount + extraCount > 0;
};

const normalizeSignOption = (value) => (
  value === 'NONE' ? 'NONE' : 'CERTIFIED_TRUE_COPY_AND_SIGN'
);

const defaultValues = {
  subject: '',
  html: '',
  supplierLabel: '제공사',
  supplier: '',
  recipientLabel: '제출처',
  recipient: '',
  siteLabel: '현장명',
  site: '',
  phrase: '',
  gclient: null,
  mailTo: [],
  mailCc: [],
};

/**
 * 템플릿 발송 — R11 + 별도 첨부(탐색기형) + SendMailDialog 메일 쓰기 동형.
 */
const SubMatTemplateSendDialog = ({ open, onClose, template, templates = null, onSent }) => {
  const dispatch = useDispatch();
  const rowsAddressBooks = useSelector((s) => s.addressBook.addressBooks);
  const sessionUser = useSelector((s) => s.session.sessionUser);

  const [items, setItems] = useState([]);
  const [extras, setExtras] = useState([]);
  const [extraSectionTitle, setExtraSectionTitle] = useState(DEFAULT_EXTRA_SECTION_TITLE);
  const [tocNumbering, setTocNumbering] = useState('DECIMAL_HIERARCHY');
  const [bodyHeaderPaging, setBodyHeaderPaging] = useState(true);
  const [templateName, setTemplateName] = useState('');
  const [templateId, setTemplateId] = useState(null);
  const [drafts, setDrafts] = useState([]);
  const [editingKey, setEditingKey] = useState(null);
  /** 두 장 이상일 때만 목록 화면. 한 장은 목차부터 연다. */
  const [multiSend, setMultiSend] = useState(false);
  const subjectAutoRef = useRef(true);
  /** 1: 목차·미리보기·별도첨부 / 2: 원본대조필·메일쓰기 */
  const [step, setStep] = useState(1);
  const tocDocPreview = useSubMatTocDocPreview(items, open);

  const [gclients, setGClients] = useState([]);
  const [mailTos, setMailTos] = useState([]);
  const [mailCcs, setMailCcs] = useState([]);
  const [mailCcInternals, setMailCcInternals] = useState([]);
  const [addressOptions, setAddressOptions] = useState([]);
  const [emailOptionsTo, setEmailOptionsTo] = useState([]);
  const [emailOptionsCc, setEmailOptionsCc] = useState([]);
  const [emailOptionsCcInternal, setEmailOptionsCcInternal] = useState([]);
  const [gclientId, setGClientId] = useState('');
  const [signOption, setSignOption] = useState('CERTIFIED_TRUE_COPY_AND_SIGN');
  const [stampLayout, setStampLayout] = useState(STAMP_LAYOUT_FULL);
  const [emptyRecipient, setEmptyRecipient] = useState(false);
  const [emptySite, setEmptySite] = useState(false);
  const [URLThumbnail, setURLThumbnail] = useState('');
  const [loading, setLoading] = useState(false);
  const [merging, setMerging] = useState(false);
  const [alertInfo, setAlertInfo] = useState({ open: false, titleAlert: '', messageAlert: '' });
  const [pdfEmptyConfirmOpen, setPdfEmptyConfirmOpen] = useState(false);
  const mailOptionsReadyRef = useRef(false);
  const mailOptionsLoadingRef = useRef(false);
  const thumbnailUrlRef = useRef('');

  const { handleSubmit, control, setValue, reset, watch, getValues } = useForm({
    defaultValues,
  });
  const htmlValue = watch('html');

  useEffect(() => {
    const Quill = ReactQuill.Quill;
    registerFonts(Quill);
  }, []);

  useEffect(() => {
    if (!open) return;
    const list = (templates && templates.length) ? templates : (template ? [template] : []);
    const nextDrafts = list.map(toSendDraft);
    const first = nextDrafts[0];
    const multi = nextDrafts.length > 1;
    setDrafts(nextDrafts);
    setMultiSend(multi);
    setSignOption(normalizeSignOption(list[0]?.signOption) || 'CERTIFIED_TRUE_COPY_AND_SIGN');
    setStampLayout(normalizeStampLayout(list[0]?.stampLayout));
    if (multi || !first) {
      setEditingKey(null);
      setItems([]);
      setExtras([]);
      setExtraSectionTitle(DEFAULT_EXTRA_SECTION_TITLE);
      setTocNumbering(normalizeTocNumbering(list[0]?.tocNumbering));
      setBodyHeaderPaging(normalizeBodyHeaderPaging(list[0]?.bodyHeaderPaging));
      setTemplateName(first?.name || '');
      setTemplateId(first?.templateId || null);
      setStep(2);
    } else {
      setEditingKey(first.key);
      setItems((first.items || []).map((it) => ({ ...it })));
      setExtras(first.extras || []);
      setExtraSectionTitle(first.extraSectionTitle || DEFAULT_EXTRA_SECTION_TITLE);
      setTocNumbering(first.tocNumbering);
      setBodyHeaderPaging(first.bodyHeaderPaging);
      setTemplateName(first.name || '');
      setTemplateId(first.templateId);
      setStep(1);
    }
    setMailTos([]);
    setMailCcs([]);
    setMailCcInternals([]);
    setEmailOptionsTo([]);
    setEmailOptionsCc([]);
    setEmailOptionsCcInternal([]);
    setGClientId('');
    setEmptyRecipient(false);
    setEmptySite(false);
    setPdfEmptyConfirmOpen(false);
    subjectAutoRef.current = true;
    mailOptionsReadyRef.current = false;
    reset({
      ...defaultValues,
    });
  }, [open, template, templates, reset]);

  // 거래선: comboData 경량 필드만 (풀 gclients 목록 회피)
  useEffect(() => {
    if (!open || !sessionUser) return;

    const phrases = sessionUser.phrases || {};
    setValue('supplierLabel', phrases.supplierLabel || '제공사');
    setValue('supplier', phrases.supplier || '');
    setValue('phrase', phrases.phrase || '');
    setValue('recipientLabel', '제출처');
    setValue('siteLabel', '현장명');

    const mailSettings = sessionUser.sessionUserLoginType === 'USER'
      ? sessionUser?.user?.mailSettings
      : sessionUser?.mailSettings;
    if (mailSettings?.footer?.useYn) {
      setValue('html', mailSettings.footer.message ?? '');
    } else {
      setValue('html', '');
    }

    let cancelled = false;
    (async () => {
      try {
        const list = await gclientActions.selectComboDataDirect(['id', 'name', 'email']);
        if (!cancelled) setGClients(list || []);
      } catch (_) {
        if (!cancelled) setGClients([]);
      }
    })();
    return () => { cancelled = true; };
  }, [open, sessionUser, setValue]);

  // 인감 썸네일: 인쇄안함 블러 미리보기에도 유지
  useEffect(() => {
    if (!open || !sessionUser?.signDocumentPath) {
      if (thumbnailUrlRef.current) {
        URL.revokeObjectURL(thumbnailUrlRef.current);
        thumbnailUrlRef.current = '';
      }
      setURLThumbnail('');
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await gclientActions.downloadDirect(sessionUser.signDocumentPath);
        const blob = await res.blob();
        if (cancelled) return;
        if (thumbnailUrlRef.current) URL.revokeObjectURL(thumbnailUrlRef.current);
        const url = URL.createObjectURL(blob);
        thumbnailUrlRef.current = url;
        setURLThumbnail(url);
      } catch (_) {
        if (!cancelled) {
          if (thumbnailUrlRef.current) URL.revokeObjectURL(thumbnailUrlRef.current);
          thumbnailUrlRef.current = '';
          setURLThumbnail('');
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, sessionUser]);

  useEffect(() => () => {
    if (thumbnailUrlRef.current) {
      URL.revokeObjectURL(thumbnailUrlRef.current);
      thumbnailUrlRef.current = '';
    }
  }, []);

  // 주소록·내부참조: 메일 영역 포커스 시 1회 로드 (redux 주소록 있으면 재호출 생략)
  const ensureMailOptionsLoaded = useCallback(async () => {
    if (!open || !sessionUser) return;
    if (mailOptionsReadyRef.current || mailOptionsLoadingRef.current) return;
    mailOptionsLoadingRef.current = true;
    try {
      if (!rowsAddressBooks?.length) {
        await dispatch(addressBookActions.selectAll());
      }
      const { id, email, name } = sessionUser;
      try {
        const users = await userActions.selectAllByGClientDirect(id);
        const emailList = [];
        if (email) {
          emailList.push({
            id: `<${name}> <${email}> <${id}>`,
            name: `회사대표메일 <${email}>`,
            group: '거래선',
          });
        }
        (users || []).forEach((user) => {
          const { name: uName, userId, gclient } = user;
          emailList.push({
            id: `<${gclient.name}> <${userId}> <${gclient.id}>`,
            name: `${uName} <${userId}>`,
            group: '거래선',
          });
        });
        setEmailOptionsCcInternal(emailList);
      } catch (_) {
        setEmailOptionsCcInternal([]);
      }
      mailOptionsReadyRef.current = true;
    } finally {
      mailOptionsLoadingRef.current = false;
    }
  }, [open, sessionUser, rowsAddressBooks, dispatch]);

  useEffect(() => {
    if (!rowsAddressBooks?.length) {
      setAddressOptions([]);
      return;
    }
    const uniqueAddresses = Array.from(
      new Map(
        rowsAddressBooks.map((address) => [
          address.email,
          {
            id: `<${address.company}> <${address.email}> <${address.id}>`,
            name: `${address.name} <${address.email}>`,
            group: '주소록',
          },
        ]),
      ).values(),
    );
    setAddressOptions(uniqueAddresses);
  }, [rowsAddressBooks]);

  const includedTemplateCount = useMemo(
    () => items.filter((it) => it.included !== false).length,
    [items],
  );
  const includedExtras = useMemo(
    () => extras.filter((e) => e.included !== false),
    [extras],
  );
  const gclientOptions = useMemo(
    () => gclients.map((g) => ({ label: g.name, id: g.id })),
    [gclients],
  );

  /** 멀티 구성 중이면 목차 수정을 버리고 메일 목록으로. 그 외에는 창을 닫는다. */
  const handleDismiss = () => {
    if (loading || merging) return;
    if (multiSend && step === 1) {
      setEditingKey(null);
      setStep(2);
      return;
    }
    onClose();
  };

  const buildExportMailData = ({ tocPreviewOnly = false } = {}) => {
    const mergePayload = buildMergePreviewPayload(items, templateName, tocNumbering, bodyHeaderPaging);
    const phrases = {
      recipient: tocPreviewOnly ? '' : (getValues('recipient') || '').trim(),
      site: tocPreviewOnly ? '' : (getValues('site') || '').trim(),
      supplierLabel: getValues('supplierLabel'),
      supplier: getValues('supplier'),
      phrase: getValues('phrase'),
    };
    const extraAttachments = includedExtras.map((e) => ({
      title: e.title,
      included: true,
    }));
    return {
      mailData: {
        name: mergePayload.name,
        tocNumbering: mergePayload.tocNumbering,
        bodyHeaderPaging: mergePayload.bodyHeaderPaging,
        items: mergePayload.items,
        folderOrder: mergePayload.folderOrder,
        extraAttachments,
        extraSectionTitle: (extraSectionTitle || '').trim() || DEFAULT_EXTRA_SECTION_TITLE,
        // 부자재 합본은 우리회사 직인만 사용 (타사 직인 대체 옵션 없음)
        myCertified: true,
        // 1단계: 목차 확인용 — 원본대조필 미적용
        options: { signOption: tocPreviewOnly ? 'NONE' : signOption, stampLayout: normalizeStampLayout(stampLayout) },
        phrases,
        stampPlacements: tocPreviewOnly
          ? {}
          : ((drafts.find((d) => d.key === editingKey) || {}).stampPlacements || {}),
      },
      addedFiles: includedExtras.map((e) => e.file),
    };
  };

  const runPreviewMerge = async ({ tocPreviewOnly = false } = {}) => {
    setMerging(true);
    try {
      const response = await dispatch(
        subMatTemplateActions.previewSendMerge(buildExportMailData({ tocPreviewOnly })),
      );
      const blob = await response.blob();
      if (!blob || blob.size === 0) {
        throw new Error('PDF로 내보내지 못했습니다.');
      }
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${(templateName || '템플릿').replace(/[\\/:*?"<>|]/g, '_')}_합본.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      setTimeout(() => window.URL.revokeObjectURL(url), 1000);
    } catch (e) {
      setAlertInfo({
        open: true,
        titleAlert: '오류',
        messageAlert:
          e?.message
          || e?.serverResponse?.message
          || 'PDF로 내보내지 못했습니다.',
      });
    } finally {
      setMerging(false);
    }
  };

  const handlePreviewMerge = () => {
    if (includedTemplateCount === 0 && includedExtras.length === 0) {
      return;
    }
    // 1단계: 목차 확인용 — 제출처·현장 검증 생략
    if (step === 1) {
      runPreviewMerge({ tocPreviewOnly: true });
      return;
    }
    if (signOption === 'CERTIFIED_TRUE_COPY_AND_SIGN'
        && normalizeStampLayout(stampLayout) !== STAMP_LAYOUT_COMPACT) {
      const recipient = (getValues('recipient') || '').trim();
      const site = (getValues('site') || '').trim();
      if (!recipient || !site) {
        setEmptyRecipient(!recipient);
        setEmptySite(!site);
        setPdfEmptyConfirmOpen(true);
        return;
      }
    }
    runPreviewMerge();
  };

  const handleAddFiles = (files) => {
    const list = Array.isArray(files) ? files : [...(files || [])];
    const pdfOnly = list.filter((f) => /\.pdf$/i.test(f.name));
    if (pdfOnly.length !== list.length) {
      setAlertInfo({
        open: true,
        titleAlert: '안내',
        messageAlert: '별도 첨부는 PDF만 추가할 수 있습니다.',
      });
    }
    if (!pdfOnly.length) return;
    setExtras((prev) => [
      ...prev,
      ...pdfOnly.map((file) => ({
        id: newExtraId(),
        file,
        title: file.name.replace(/\.pdf$/i, ''),
        included: true,
      })),
    ]);
  };

  const handleChangeGClient = (_e, value) => {
    if (!value) {
      setValue('recipient', '');
      setGClientId('');
      setEmailOptionsTo([]);
      setEmailOptionsCc([]);
      setMailTos([]);
      setMailCcs([]);
      setValue('mailTo', []);
      setValue('mailCc', []);
      return;
    }
    const { id: selectedId, label } = value;
    // 제출처만 동기 반영 — Autocomplete 선택 완료 전에 To/Cc remount·비동기 로드가 끼면 첫 클릭이 씹힘
    setValue('recipient', label);
    setEmptyRecipient(false);
    setMailTos([]);
    setMailCcs([]);
    setValue('mailTo', []);
    setValue('mailCc', []);

    window.setTimeout(() => {
      setGClientId(selectedId);
      ensureMailOptionsLoaded();
      (async () => {
        try {
          const users = await userActions.selectAllByGClientDirect(selectedId);
          const gclient = gclients.find((g) => g.id === selectedId);
          const emailList = [];
          if (gclient?.email) {
            emailList.push({
              id: `<${gclient.name}> <${gclient.email}> <${gclient.id}>`,
              name: `회사대표메일 <${gclient.email}>`,
              group: '거래선',
            });
          }
          (users || []).forEach((user) => {
            const { name: uName, userId, gclient: gc } = user;
            emailList.push({
              id: `<${gc.name}> <${userId}> <${gc.id}>`,
              name: `${uName} <${userId}>`,
              group: '거래선',
            });
          });
          setEmailOptionsTo(emailList);
          setEmailOptionsCc(emailList);
        } catch (_) {
          setEmailOptionsTo([]);
          setEmailOptionsCc([]);
        }
      })();
    }, 0);
  };

  const handleChangeRecipient = (e) => {
    const { value } = e.target;
    setEmptyRecipient(!value);
    setValue('recipient', value);
  };

  const handleChangeSite = (e) => {
    const { value } = e.target;
    setEmptySite(!value);
    setValue('site', value);
    if (subjectAutoRef.current) {
      setValue('subject', (value || '').trim());
    }
  };

  const openCompose = (key) => {
    const draft = drafts.find((d) => d.key === key);
    if (!draft) return;
    setItems((draft.items || []).map((it) => ({ ...it })));
    setExtras(draft.extras || []);
    setExtraSectionTitle(draft.extraSectionTitle || DEFAULT_EXTRA_SECTION_TITLE);
    setTocNumbering(draft.tocNumbering);
    setBodyHeaderPaging(draft.bodyHeaderPaging);
    setTemplateName(draft.name || '');
    setTemplateId(draft.templateId);
    setEditingKey(key);
    setStep(1);
  };

  const moveDraft = (from, to) => {
    if (to < 0 || to >= drafts.length || from === to) return;
    setDrafts((prev) => {
      const copy = [...prev];
      const [row] = copy.splice(from, 1);
      copy.splice(to, 0, row);
      return copy;
    });
  };

  const onDragEndDraft = (result) => {
    if (!result.destination) return;
    moveDraft(result.source.index, result.destination.index);
  };

  const removeDraft = (key) => {
    setDrafts((prev) => (prev.length <= 1 ? prev : prev.filter((d) => d.key !== key)));
  };

  const returnToMail = () => {
    if (includedTemplateCount === 0 && includedExtras.length === 0) {
      setAlertInfo({
        open: true,
        titleAlert: '안내',
        messageAlert: '발송에 포함할 목차 항목 또는 별도 첨부가 없습니다.',
      });
      return;
    }
    setDrafts((prev) => prev.map((d) => (
      d.key === editingKey
        ? {
          ...d,
          items,
          extras,
          extraSectionTitle,
          name: templateName,
        }
        : d
    )));
    if (multiSend) setEditingKey(null);
    setStep(2);
  };

  const onSubmit = async (form) => {
    const {
      subject,
      html,
      supplierLabel,
      supplier,
      recipient,
      site,
      phrase,
    } = form;

    if (!mailTos?.length) {
      setAlertInfo({
        open: true,
        titleAlert: '안내',
        messageAlert: '받는 사람 메일 주소를 입력해 주세요.',
      });
      return;
    }
    const emptyDraft = drafts.find((d) => !draftHasContent(d));
    if (!drafts.length || emptyDraft) {
      setAlertInfo({
        open: true,
        titleAlert: '안내',
        messageAlert: emptyDraft
          ? `"${emptyDraft.name || '템플릿'}"에 포함할 목차 항목 또는 별도 첨부가 없습니다.`
          : '보낼 템플릿을 선택해 주세요.',
      });
      return;
    }
    if (signOption === 'CERTIFIED_TRUE_COPY_AND_SIGN'
        && normalizeStampLayout(stampLayout) !== STAMP_LAYOUT_COMPACT) {
      if (!recipient?.trim() || !site?.trim()) {
        setEmptyRecipient(!recipient?.trim());
        setEmptySite(!site?.trim());
        setAlertInfo({
          open: true,
          titleAlert: '안내',
          messageAlert: '원본대조필 사용 시 제출처·현장명을 입력해 주세요.',
        });
        return;
      }
    }

    const firstTo = mailTos[0];
    const gclientName = firstTo?.id?.match?.(/<([^>]+)>/)?.[1]
      || firstTo?.name?.replace(/<[^>]+>/, '').trim()
      || '';

    const filesToUpload = [];
    const templatePayloads = drafts.map((draft) => {
      const included = (draft.extras || []).filter((e) => e.included !== false && e.file);
      included.forEach((e) => filesToUpload.push(e.file));
      const mergePayload = buildMergePreviewPayload(
        draft.items,
        draft.name,
        draft.tocNumbering,
        draft.bodyHeaderPaging,
      );
      return {
        templateId: draft.templateId,
        name: mergePayload.name,
        tocNumbering: mergePayload.tocNumbering,
        bodyHeaderPaging: mergePayload.bodyHeaderPaging,
        items: mergePayload.items,
        folderOrder: mergePayload.folderOrder,
        extraAttachments: included.map((e) => ({ title: e.title, included: true })),
        extraFileCount: included.length,
        extraSectionTitle: (draft.extraSectionTitle || '').trim() || DEFAULT_EXTRA_SECTION_TITLE,
        stampPlacements: draft.stampPlacements || {},
      };
    });

    setLoading(true);
    try {
      const data = await dispatch(subMatTemplateActions.send({
        mailData: {
          templates: templatePayloads,
          mailTo: mailTos,
          mailCc: mailCcs || [],
          mailCcInternal: mailCcInternals || [],
          subject,
          html,
          sendReplace: false,
          myCertified: true,
          gclientTo: { name: gclientName },
          options: { signOption, stampLayout: normalizeStampLayout(stampLayout) },
          phrases: {
            recipient: recipient?.trim() || '',
            site: site?.trim() || '',
            supplierLabel,
            supplier,
            phrase,
          },
        },
        addedFiles: filesToUpload,
      }));
      setAlertInfo({
        open: true,
        titleAlert: '안내',
        messageAlert: '메일이 전송되었습니다.',
        handleClose: () => {
          if (onSent) onSent(data?.gsendG04docu);
          onClose();
        },
      });
    } catch (e) {
      setAlertInfo({
        open: true,
        titleAlert: '안내',
        messageAlert: e?.message || '발송에 실패했습니다.',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={handleDismiss}
        fullWidth
        maxWidth={false}
        PaperProps={{ sx: DIALOG_PAPER_SX }}
        aria-labelledby="submat-send-dialog-title"
      >
        <DialogTitleClose
          id="submat-send-dialog-title"
          onClose={handleDismiss}
          sx={{ m: 0, px: 2, pt: 2, pb: 1.5 }}
        >
          부자재 자재승인서 메일전송
        </DialogTitleClose>
        <DialogContent
          dividers
          sx={{
            px: 2,
            // theme.js `.MuiDialogContent-root { padding-top: 10px !important }` 덮어씀
            pt: '16px !important',
            pb: '16px !important',
          }}
        >
          {step === 1 && (
            <Box
              sx={{
                minHeight: STEP1_WORK_MIN_HEIGHT,
                height: { md: `min(70vh, ${STEP1_WORK_MIN_HEIGHT + 80}px)` },
                overflow: 'hidden',
              }}
            >
              <Grid container spacing={2} alignItems="stretch" sx={{ height: '100%' }}>
                <Grid
                  item
                  xs={12}
                  md={6}
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 2,
                    minHeight: 0,
                    height: { md: '100%' },
                  }}
                >
                  <SubMatTemplateSendSection
                    title={`목차 구성${templateName ? ` — ${templateName}` : ''}`}
                    sx={{ flex: 1, minHeight: 0 }}
                    contentSx={{ flex: 1, minHeight: 0, overflow: 'auto' }}
                  >
                    <SubMatTemplateTocTree
                      items={items}
                      onChange={setItems}
                      sendMode
                      bare
                      onPreview={tocDocPreview.previewItem}
                      previewLoadingId={tocDocPreview.loadingId}
                    />
                  </SubMatTemplateSendSection>
                  <SubMatTemplateExtraAttachmentsList
                    extras={extras}
                    onChange={setExtras}
                    onAddFiles={handleAddFiles}
                    sectionTitle={extraSectionTitle}
                    onSectionTitleChange={setExtraSectionTitle}
                  />
                </Grid>
                <Grid
                  item
                  xs={12}
                  md={6}
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    minHeight: 0,
                    height: { md: '100%' },
                  }}
                >
                  <SubMatTemplateSendSection
                    title={(
                      <SubMatTemplateTocPreviewTitle
                        pane={tocDocPreview.pane}
                        onPane={tocDocPreview.setPane}
                        docTitle={tocDocPreview.doc?.title}
                        onCloseDoc={tocDocPreview.closeDoc}
                      />
                    )}
                    sx={{ flex: 1, minHeight: 0, height: '100%' }}
                    contentSx={{
                      display: 'flex',
                      flexDirection: 'column',
                      minHeight: 0,
                      bgcolor: tocDocPreview.pane === 'doc' ? 'background.paper' : 'grey.700',
                      p: 0,
                      boxSizing: 'border-box',
                    }}
                  >
                    {tocDocPreview.pane === 'doc' ? (
                      <SubMatTemplateTocDocFrame
                        url={tocDocPreview.doc?.url}
                        error={tocDocPreview.error}
                        loading={Boolean(tocDocPreview.loadingId)}
                      />
                    ) : (
                      <SubMatTemplateMergedTocPreview
                        fillHeight
                        bare
                        documentLook
                        items={items}
                        tocNumbering={tocNumbering}
                        extraAttachments={extras}
                        extraSectionTitle={extraSectionTitle}
                      />
                    )}
                  </SubMatTemplateSendSection>
                </Grid>
              </Grid>
            </Box>
          )}

          {step === 2 && (
            <Grid
              container
              spacing={2}
              alignItems="stretch"
              sx={{
                minHeight: { md: 420 },
              }}
            >
              {multiSend && (
              <Grid item xs={12} md={4} sx={{ display: 'flex' }}>
                <SubMatTemplateSendSection
                  title={`보낼 템플릿 ${drafts.length}건`}
                  sx={{ flex: 1, width: '100%' }}
                  contentSx={{ p: 0, overflow: 'auto' }}
                >
                  <DragDropContext onDragEnd={onDragEndDraft}>
                    <Droppable droppableId="send-templates">
                      {(dropProvided) => (
                        <List
                          dense
                          disablePadding
                          ref={dropProvided.innerRef}
                          {...dropProvided.droppableProps}
                        >
                          {drafts.map((draft, index) => (
                            <Draggable key={draft.key} draggableId={draft.key} index={index}>
                              {(dragProvided, snapshot) => (
                                <ListItem
                                  ref={dragProvided.innerRef}
                                  {...dragProvided.draggableProps}
                                  sx={{
                                    alignItems: 'center',
                                    py: 0.5,
                                    px: 0.5,
                                    bgcolor: snapshot.isDragging ? 'action.hover' : 'transparent',
                                  }}
                                >
                                  <IconButton
                                    size="small"
                                    aria-label="순서 변경"
                                    {...dragProvided.dragHandleProps}
                                    sx={{ cursor: 'grab', mr: 0.25 }}
                                  >
                                    <DragIndicator sx={{ fontSize: 18 }} />
                                  </IconButton>
                                  <ListItemText
                                    primary={draft.name || '템플릿'}
                                    primaryTypographyProps={{ variant: 'body2', noWrap: true }}
                                    secondary={draftHasContent(draft) ? '저장된 목차' : '포함할 항목 없음'}
                                    secondaryTypographyProps={{ variant: 'caption' }}
                                    sx={{ my: 0 }}
                                  />
                                  <Box sx={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
                                    <IconButton
                                      size="small"
                                      aria-label="구성"
                                      onClick={() => openCompose(draft.key)}
                                    >
                                      <Edit sx={{ fontSize: 16 }} />
                                    </IconButton>
                                    <IconButton
                                      size="small"
                                      aria-label="빼기"
                                      disabled={drafts.length <= 1}
                                      onClick={() => removeDraft(draft.key)}
                                    >
                                      <Delete sx={{ fontSize: 16 }} />
                                    </IconButton>
                                  </Box>
                                </ListItem>
                              )}
                            </Draggable>
                          ))}
                          {dropProvided.placeholder}
                        </List>
                      )}
                    </Droppable>
                  </DragDropContext>
                </SubMatTemplateSendSection>
              </Grid>
              )}
              <Grid item xs={12} md={multiSend ? 8 : 12}>
                <Grid container spacing={2} alignItems="stretch" sx={{ height: '100%' }}>
              <Grid item xs={12} md={6} sx={{ display: 'flex' }}>
                <SubMatTemplateSendSection
                  title="원본대조필"
                  sx={{ flex: 1, width: '100%' }}
                  contentSx={{
                    p: 2,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    minHeight: 0,
                  }}
                >
                  <CertifiedStampPrintPane
                    signOption={signOption}
                    stampLayout={stampLayout}
                    onChangeSignOption={setSignOption}
                    onChangeStampLayout={setStampLayout}
                    control={control}
                    signThumbnailUrl={URLThumbnail}
                    emptyRecipient={emptyRecipient}
                    emptySite={emptySite}
                    onChangeRecipient={handleChangeRecipient}
                    onChangeSite={handleChangeSite}
                    captionCompact="원본대조필과 인감만 인쇄합니다."
                    captionFull="제출처·현장명은 메일 전송 시 필수입니다."
                  >
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
                      원본대조필 위치는 각 템플릿에 저장된 위치를 사용합니다.
                    </Typography>
                  </CertifiedStampPrintPane>
                </SubMatTemplateSendSection>
              </Grid>

              <Grid item xs={12} md={6} sx={{ display: 'flex' }}>
                <SubMatTemplateSendSection
                  title="메일 쓰기"
                  sx={{ flex: 1, width: '100%', overflow: 'visible' }}
                  contentSx={{ p: 2, overflow: 'visible' }}
                >
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <FormInputAutoComplete
                        key={`gclient-${open}-${templateId || ''}`}
                        name="gclient"
                        control={control}
                        label="거래선명"
                        options={gclientOptions}
                        setValue={setValue}
                        defaultValue={null}
                        onCustomChange={handleChangeGClient}
                        isOptionEqualToValue={(a, b) => a?.id === b?.id}
                        getOptionLabel={(o) => (typeof o === 'string' ? o : (o?.label || ''))}
                        ListboxProps={{
                          onMouseDown: (e) => e.preventDefault(),
                        }}
                        componentsProps={{
                          popper: { disablePortal: true },
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} onFocus={() => { ensureMailOptionsLoaded(); }}>
                      <CustomInputSelect
                        key={`to-${open}-${templateId || ''}-${gclientId}`}
                        defaultOptions={emailOptionsTo}
                        addressOptions={addressOptions}
                        label="받는 사람"
                        placeholder="이메일 선택 또는 직접 입력하세요."
                        onChange={(value) => {
                          setMailTos(value);
                          setValue('mailTo', value);
                        }}
                        width="100%"
                        size="small"
                      />
                    </Grid>
                    <Grid item xs={12} onFocus={() => { ensureMailOptionsLoaded(); }}>
                      <CustomInputSelect
                        key={`cc-${open}-${templateId || ''}-${gclientId}`}
                        defaultOptions={emailOptionsCc}
                        addressOptions={addressOptions}
                        label="참조"
                        placeholder="이메일 선택 또는 직접 입력하세요."
                        onChange={(value) => {
                          setMailCcs(value);
                          setValue('mailCc', value);
                        }}
                        width="100%"
                        size="small"
                      />
                    </Grid>
                    <Grid item xs={12} onFocus={() => { ensureMailOptionsLoaded(); }}>
                      <CustomInputSelect
                        key={`cci-${open}-${templateId || ''}`}
                        defaultOptions={emailOptionsCcInternal}
                        addressOptions={[]}
                        label="내부참조"
                        placeholder="이메일 선택 또는 직접 입력하세요."
                        onChange={(value) => {
                          setMailCcInternals(value);
                        }}
                        width="100%"
                        size="small"
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <FormInputText
                        name="subject"
                        control={control}
                        label="제목"
                        onCustomChange={() => { subjectAutoRef.current = false; }}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Box sx={smallStyle}>
                        <Typography
                          variant="subtitle1"
                          sx={{
                            marginBottom: 1,
                            fontWeight: 500,
                            color: 'rgba(0, 0, 0, 0.87)',
                          }}
                        />
                        <ReactQuill
                          value={htmlValue || ''}
                          onChange={(content) => setValue('html', content)}
                          modules={fontModules}
                          formats={fontFormats}
                          placeholder="메시지를 입력하세요..."
                          theme="snow"
                          style={{
                            height: '150px',
                            marginBottom: '20px',
                          }}
                        />
                      </Box>
                    </Grid>
                  </Grid>
                </SubMatTemplateSendSection>
              </Grid>
                </Grid>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <SubMatTemplateDialogFooter
          cancelDisabled={loading || merging}
          cancelLabel={multiSend && step === 1 ? '취소' : '닫기'}
          onCancel={handleDismiss}
          beforePrimary={step === 2 && !multiSend ? (
            <SubMatDialogFooterButton
              variant="outlined"
              onClick={() => setStep(1)}
              disabled={loading || merging}
            >
              이전
            </SubMatDialogFooterButton>
          ) : null}
          pdfButton={step === 1 ? (
            <SubMatDialogFooterButton
              variant="outlined"
              startIcon={<PictureAsPdf />}
              loading={merging}
              disabled={loading}
              onClick={handlePreviewMerge}
            >
              PDF 보기
            </SubMatDialogFooterButton>
          ) : null}
          primaryButton={step === 1 ? (
            <SubMatDialogFooterButton
              variant="contained"
              onClick={returnToMail}
              disabled={loading || merging}
            >
              {multiSend ? '메일로' : '다음'}
            </SubMatDialogFooterButton>
          ) : (
            <SubMatDialogFooterButton
              variant="contained"
              loading={loading}
              disabled={merging}
              onClick={handleSubmit(onSubmit)}
            >
              보내기
            </SubMatDialogFooterButton>
          )}
        />
      </Dialog>

      <ConfirmDialog
        title="안내"
        open={pdfEmptyConfirmOpen}
        setOpen={setPdfEmptyConfirmOpen}
        onConfirm={() => runPreviewMerge()}
        onCancel={() => {}}
        confirmLabel="확인"
        cancelLabel="취소"
      >
        제출처·현장명이 비어 있습니다. 이 PDF를 그대로 외부에 전달하면 원본대조필 정보가 불완전할 수 있습니다. 계속할까요?
      </ConfirmDialog>

      <AlertDialog
        alertInfo={alertInfo}
        setAlertInfo={setAlertInfo}
      />
    </>
  );
};

export default SubMatTemplateSendDialog;
